import { useState } from "react";
import { Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAppearance, themeVars } from "@/context/AppearanceContext";

const GAMES = { royal_dream: "Royal Dream", higgs_domino: "Higgs Domino" };
const PRESETS = [["Emas JP", "#e5b84a", "#b88a2e"], ["Merah Royal", "#ef4444", "#991b1b"], ["Biru Neon", "#38bdf8", "#1d4ed8"], ["Hijau", "#22c55e", "#15803d"], ["Ungu", "#a855f7", "#6b21a8"]];

const useSaver = (url, msg) => {
  const { reload } = useAppearance();
  const [busy, setBusy] = useState(false);
  const save = async (body) => {
    setBusy(true);
    try {
      await api.put(url, body);
      await reload();
      toast.success(msg);
    } catch (err) {
      toast.error(errMsg(err, "Gagal menyimpan"));
    } finally {
      setBusy(false);
    }
  };
  return [busy, save];
};

const SaveBtn = ({ busy, testid, children }) => (
  <button type="submit" disabled={busy} className="rk-btn-primary mt-5 inline-flex items-center gap-2 text-sm" data-testid={testid}>
    {busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} {children}
  </button>
);

const L = ({ label, children }) => (
  <label className="block"><span className="text-[11px] uppercase tracking-widest text-zinc-500">{label}</span><div className="mt-1.5">{children}</div></label>
);

export const TextsForm = ({ canManage }) => {
  const { texts } = useAppearance();
  const [f, setF] = useState(texts);
  const [busy, save] = useSaver("/admin/appearance/texts", "Teks informasi disimpan");
  const setFeat = (i, k, v) => setF((x) => ({ ...x, features: x.features.map((ft, j) => (j === i ? { ...ft, [k]: v } : ft)) }));
  const setProd = (g, k, v) => setF((x) => ({ ...x, products: { ...x.products, [g]: { ...x.products[g], [k]: v } } }));
  return (
    <form onSubmit={(e) => { e.preventDefault(); save(f); }} className="rk-glass rounded-2xl p-6" data-testid="texts-form">
      <div className="grid gap-4 md:grid-cols-2">
        <L label="Judul layanan"><input value={f.services_title} onChange={(e) => setF({ ...f, services_title: e.target.value })} disabled={!canManage} maxLength={60} className="rk-input" data-testid="text-services-title" /></L>
        <L label="Tagline brand"><input value={f.brand_tagline} onChange={(e) => setF({ ...f, brand_tagline: e.target.value })} disabled={!canManage} maxLength={80} className="rk-input" data-testid="text-brand-tagline" /></L>
        <div className="md:col-span-2"><L label="Sub judul layanan"><input value={f.services_subtitle} onChange={(e) => setF({ ...f, services_subtitle: e.target.value })} disabled={!canManage} maxLength={160} className="rk-input" data-testid="text-services-subtitle" /></L></div>
        {Object.entries(GAMES).map(([g, n]) => (
          <div key={g} className="grid grid-cols-[1fr_110px] gap-2">
            <L label={`Deskripsi kartu ${n}`}><input value={f.products[g].sub} onChange={(e) => setProd(g, "sub", e.target.value)} disabled={!canManage} maxLength={80} className="rk-input" data-testid={`text-product-sub-${g}`} /></L>
            <L label="Badge"><input value={f.products[g].badge} onChange={(e) => setProd(g, "badge", e.target.value)} disabled={!canManage} maxLength={12} className="rk-input" data-testid={`text-product-badge-${g}`} /></L>
          </div>
        ))}
        {f.features.map((ft, i) => (
          <div key={i} className="grid grid-cols-[1fr_1.4fr] gap-2 md:col-span-2">
            <L label={`Keunggulan ${i + 1}`}><input value={ft.title} onChange={(e) => setFeat(i, "title", e.target.value)} disabled={!canManage} maxLength={40} className="rk-input" data-testid={`text-feature-title-${i}`} /></L>
            <L label="Keterangan"><input value={ft.desc} onChange={(e) => setFeat(i, "desc", e.target.value)} disabled={!canManage} maxLength={120} className="rk-input" data-testid={`text-feature-desc-${i}`} /></L>
          </div>
        ))}
      </div>
      {canManage ? <SaveBtn busy={busy} testid="texts-save">Simpan Teks</SaveBtn> : null}
    </form>
  );
};

const ThemePreview = ({ t }) => (
  <div className="rounded-2xl border border-white/10 bg-black p-5" style={themeVars(t)} data-testid="theme-preview">
    <div className="text-2xl" style={{ fontFamily: `var(--jp-heading-font)`, color: "var(--jp-primary)" }}>PILIH LAYANAN JP STORE</div>
    <p className="mt-1 text-sm text-white/80" style={{ fontFamily: "var(--jp-body-font)" }}>Top up dan bongkar melalui halaman resmi.</p>
    <div className="mt-4 flex flex-wrap gap-2">
      <span className="kz-gold-btn px-5 py-2 text-xs font-extrabold text-black">ADMIN KHUSUS KOMPLAIN</span>
      <span className="kz-pill border-white/25 bg-gradient-to-r from-[#16a34a] to-[#22c55e] text-white">JOIN SALURAN</span>
    </div>
  </div>
);

export const ThemeForm = ({ canManage }) => {
  const { theme, fonts } = useAppearance();
  const [t, setT] = useState(theme);
  const [busy, save] = useSaver("/admin/appearance/theme", "Tema disimpan & langsung tampil di halaman member");
  const color = (k) => (
    <div className="flex items-center gap-2">
      <input type="color" value={t[k]} onChange={(e) => setT({ ...t, [k]: e.target.value })} disabled={!canManage} className="h-10 w-12 cursor-pointer rounded border border-white/10 bg-transparent" data-testid={`theme-${k}-picker`} />
      <input value={t[k]} onChange={(e) => setT({ ...t, [k]: e.target.value })} disabled={!canManage} maxLength={7} className="rk-input !w-28 font-mono" data-testid={`theme-${k}-input`} />
    </div>
  );
  return (
    <form onSubmit={(e) => { e.preventDefault(); save(t); }} className="rk-glass grid gap-6 rounded-2xl p-6 lg:grid-cols-2" data-testid="theme-form">
      <div className="space-y-4">
        <div className="flex flex-wrap gap-1.5">
          {PRESETS.map(([n, p, s]) => (
            <button key={n} type="button" disabled={!canManage} onClick={() => setT({ ...t, primary: p, secondary: s })} className="inline-flex items-center gap-1.5 rounded-full border border-white/10 px-3 py-1 text-[11px] text-zinc-300 hover:border-white/30" data-testid={`theme-preset-${n}`}>
              <span className="h-3 w-3 rounded-full" style={{ background: p }} /> {n}
            </button>
          ))}
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <L label="Warna utama">{color("primary")}</L>
          <L label="Warna pendukung">{color("secondary")}</L>
          {[["heading_font", "Font judul"], ["body_font", "Font isi"]].map(([k, l]) => (
            <L key={k} label={l}>
              <select value={t[k]} onChange={(e) => setT({ ...t, [k]: e.target.value })} disabled={!canManage} className="rk-input" data-testid={`theme-${k}`}>
                {fonts.map((fn) => <option key={fn} value={fn}>{fn}</option>)}
              </select>
            </L>
          ))}
        </div>
        <L label="Gaya tombol">
          <div className="flex gap-2" data-testid="theme-button-style">
            {[["pill", "Pill"], ["rounded", "Membulat"], ["square", "Kotak"]].map(([k, l]) => (
              <button key={k} type="button" disabled={!canManage} onClick={() => setT({ ...t, button_style: k })} data-testid={`theme-btn-${k}`}
                className={`px-4 py-1.5 text-xs ${k === "pill" ? "rounded-full" : k === "rounded" ? "rounded-lg" : "rounded-sm"} ${t.button_style === k ? "bg-[#e5b80b] font-semibold text-black" : "border border-white/10 text-zinc-400"}`}>{l}</button>
            ))}
          </div>
        </L>
        {canManage ? (
          <div className="flex gap-2">
            <SaveBtn busy={busy} testid="theme-save">Simpan Tema</SaveBtn>
            <button type="button" onClick={() => setT({ primary: "#e5b84a", secondary: "#b88a2e", heading_font: "Lilita One", body_font: "Nunito", button_style: "pill" })} className="rk-btn-ghost mt-5 text-sm" data-testid="theme-reset">Kembalikan bawaan</button>
          </div>
        ) : null}
      </div>
      <ThemePreview t={t} />
    </form>
  );
};
